import { datadogLogs } from '@datadog/browser-logs';
import type { LogsEvent } from '@datadog/browser-logs';
import { LogType } from '../types/logType';

// Third-party calls the Incode SDK makes on the side (IP lookup, DNS-over-HTTPS
// check, telemetry, device fingerprint). The SDK tolerates them failing, so a
// dropped request there is not a liveness failure — keep it as a warning so
// it stops inflating the error counts.
const NON_CRITICAL_REQUEST_URLS = [
  'https://api.ipify.org/',
  'https://cloudflare-dns.com/',
  '/omni/interview-events',
  '/omni/add/device-fingerprint',
];

// Applied to every log before it leaves the browser. Only touches the
// automatic "Fetch error …" / "XHR error …" logs (origin: network) that
// forwardErrorsToLogs creates for status-0 and 5xx responses.
export function filterNetworkErrorLog(log: LogsEvent, context?: { isAborted?: boolean }): boolean {
  if (log.origin !== 'network') return true;
  // Aborted on purpose (navigation, unmount, SDK cancelling) — not a failure.
  if (context?.isAborted) return false;
  const url = (log.http as { url?: string } | undefined)?.url ?? '';
  if (NON_CRITICAL_REQUEST_URLS.some((pattern) => url.includes(pattern))) {
    log.status = 'warn';
  }
  return true;
}

export class DatadogLogger {
  private static initialized = false;

  static Initialize(
    clientToken: string,
    site: string,
    service: string,
    env: string,
  ): void {
    if (DatadogLogger.initialized) return;
    datadogLogs.init({
      clientToken,
      site,
      service,
      env,
      forwardErrorsToLogs: true,
      beforeSend: (log, context) => filterNetworkErrorLog(log, context as { isAborted?: boolean }),
      sessionSampleRate: 100,
    });
    DatadogLogger.initialized = true;
  }

  static Log(message: string, context: object, type: LogType): void {
    if (!DatadogLogger.initialized) return;
    switch (type) {
      case LogType.ERROR:
        datadogLogs.logger.error(message, context);
        break;
      case LogType.WARN:
        datadogLogs.logger.warn(message, context);
        break;
      case LogType.DEBUG:
        datadogLogs.logger.debug(message, context);
        break;
      default:
        datadogLogs.logger.info(message, context);
    }
  }
}
